/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import {Input} from "pages/Front/Booking/components/BookingInput";
import {useTranslation} from "react-i18next";
import {ErrorOption, FieldPath} from "react-hook-form";
import {VerificationType} from "types/enums/VerificationType";
import {toast} from "react-toastify";
import clsx from "clsx";
import {PaperAirplaneIcon} from "@heroicons/react/24/outline";
import {useRequiredField, useValueVerificationForShop} from "utils/bookingUtils";
import {emailValidation} from "utils/utility";
import {ObjectSchema} from "yup";
import {VerificationStatusResponse} from "types/responses/VerificationStatusResponse";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import Lottie from "lottie-react";
import SuccessAnimation from "components/Animations/SuccessAnimation.json";
import {StrongText} from "pages/Front/components/TextStyles/StrongText";
import BookingAction from "store/booking/BookingAction";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";

const VerificationCardForShop = (props: {
    errors: any;
    register: any;
    defaultValues: any;
    schema: ObjectSchema<any>;
    setValue: (name, value) => void;
    setError: (name: FieldPath<CompanyInputs>, error: ErrorOption, options?: {shouldFocus: boolean}) => void;
}) => {
    const emailVerified = useTypedSelector(state => state.bookingState.bookingProcess.emailVerified);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isMailSubmitted, setIsMailSubmitted] = useState(false);
    const [resultVerification, setResultVerification] = useState<VerificationStatusResponse | null>(null);

    const [verificationCode, setVerificationCode] = useState<number | null>(null);
    const [emailValue, setEmailValue] = useState<string>("");
    const valueVerification = useValueVerificationForShop();
    const requiredField = useRequiredField(props.schema);

    const submitEmailHandler = async () => {
        const verifiedValue = emailValue;

        if (!verifiedValue) {
            props.setError(
                "email",
                {
                    type: "error",
                    message: "Email is required",
                },
                {shouldFocus: true}
            );
            toast.error(t("Email is required"));
            return Promise.reject("Email is required");
        }

        if (!emailValidation(verifiedValue)) {
            props.setError(
                "email",
                {
                    type: "error",
                    message: "Invalid email format",
                },
                {shouldFocus: true}
            );
            toast.error(t("Invalid email format"));
            return Promise.reject("Invalid email format!");
        }

        return await valueVerification.sendVerification(verifiedValue, VerificationType.Email).then(resultVerificationResponse => {
            setIsMailSubmitted(true);
            setVerificationCode(null);
            setResultVerification(resultVerificationResponse);
            return resultVerificationResponse;
        });
    };

    const setEmailValueHandler = (email: string) => {
        setEmailValue(email);
        if (emailVerified) {
            dispatch(BookingAction.setEmailVerified(false));
        }
        setIsMailSubmitted(false);
        props.setValue("email", email);
    };
    const setVerificationCodeHandler = (value: number) => {
        setVerificationCode(value);
        if (value.toString().length === 4) {
            submitCodeHandler(value).then(r => {
                if (r) {
                    console.log("hhhh");
                    dispatch(BookingAction.setEmailVerified(true));
                    setIsMailSubmitted(true);
                } else {
                    dispatch(BookingAction.setEmailVerified(false));
                    setIsMailSubmitted(false);
                }
            });
        }
    };
    const submitCodeHandler = async (verificationCode: number) => {
        // const data = e.target.name;
        if (!resultVerification || !verificationCode) {
            toast.error(t("Submit email for verification first"));
            return;
        }

        const data = {
            request_id: resultVerification.verification_request_id,
            type: resultVerification.verification_method as VerificationType,
            verification_code: "" + verificationCode,
        };
        // data_2: {request_id: string; verification_code: string; type: VerificationType},
        return await valueVerification.handleVerification(data, resultVerification);
    };

    useEffect(() => {
        setEmailValueHandler(props.defaultValues.email);
    }, []);

    useEffect(() => {
        if (resultVerification && resultVerification.status === 2) {
            dispatch(BookingAction.setEmailVerified(true));
            setIsMailSubmitted(true);
        }
    }, [resultVerification]);

    return (
        <div className={"VerificationCardForShop basis-[50%] flex-shrink-1 flex-grow-1 flex flex-col space-y-6"}>
            <CardComponent>
                <CardLabel title={t("E-mail verification")} />
                <Input
                    className={"w-full pl-10"}
                    disabled={isMailSubmitted}
                    errors={props.errors}
                    label={t("E-mail")}
                    name={"email"}
                    register={props.register}
                    required={requiredField.testField("email")}
                    type={"email"}
                    onChange={e => setEmailValueHandler(e.target.value)}
                    onEnter={e => {
                        if (e.key === "Enter") {
                            submitEmailHandler();
                        }
                    }}
                />
                {!emailVerified ? (
                    <>
                        <button
                            className={clsx(
                                !isMailSubmitted ? "bg-cta-800" : "bg-primary-800 disabled",
                                "mt-3 inline-flex w-full items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            )}
                            disabled={isMailSubmitted}
                            type="button"
                            onClick={submitEmailHandler}
                        >
                            <PaperAirplaneIcon className={"w-5 h-5 mr-2"} />
                            <span className={"first-letter:uppercase"}>{!isMailSubmitted ? t("verify") : t("Enter verification code")}</span>
                        </button>

                        <div className="w-full mt-2">
                            <label className="sr-only" htmlFor="verification_code">
                                Verification Code
                            </label>
                            <input
                                className="block w-full rounded-md border py-1.5 text-center text-gray-900 shadow-sm active:ring-cta-800 active:ring-2 active:ring-offset-2 focus:border-cta-800 checked:bg-cta-800 sm:text-sm border-gray-800 placeholder:text-gray-800 sm:leading-6"
                                id="verification_code"
                                maxLength={16}
                                minLength={4}
                                name={"verification_code"}
                                placeholder="* * * *"
                                type="text"
                                value={verificationCode ?? ""}
                                onChange={e => setVerificationCodeHandler(+e.target.value)}
                            />
                            <p>{t("enter_code_via", {type: "E-mail"})}</p>
                            <div className={"mt-4 italic text-sm leading-4"}>{t("Please check your spam / junk box if you haven't received the email.")}</div>
                        </div>
                    </>
                ) : (
                    <div className={"mt-4"}>
                        <div className={"flex flex-row justify-start items-center space-x-4"}>
                            <div className={"w-10 h-10 rounded-full shadow-lg p-1 bg-green-100"}>
                                <Lottie animationData={SuccessAnimation} loop={false} onComplete={() => {}} />
                            </div>
                            <StrongText text={t("Your email has been verified successfully")} />
                        </div>
                    </div>
                )}
            </CardComponent>
        </div>
    );
};

export default VerificationCardForShop;
